import React, { useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { orderAPI, paymentAPI } from '../services/api';

const formatDateInput = (date) => {
  const value = new Date(date);
  const year = value.getFullYear();
  const month = `${value.getMonth() + 1}`.padStart(2, '0');
  const day = `${value.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getDefaultDeliveryDate = () => {
  const today = new Date();
  const futureDate = new Date(today);
  futureDate.setDate(futureDate.getDate() + 28);
  return formatDateInput(futureDate);
};

const getMinimumDeliveryDate = () => {
  const today = new Date();
  const futureDate = new Date(today);
  futureDate.setDate(futureDate.getDate() + 15);
  return formatDateInput(futureDate);
};

const getDeliveryDiscount = (subtotal) => {
  if (subtotal >= 15000) return 750;
  if (subtotal >= 11500) return 500;
  if (subtotal >= 7500) return 400;
  return 0;
};

export default function Checkout() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { cart, clearCart } = useCart();
  const [form, setForm] = useState(() => {
    const storedDate = localStorage.getItem('starlight-delivery-date');
    const savedDate = storedDate && storedDate >= getMinimumDeliveryDate() ? storedDate : getDefaultDeliveryDate();
    return {
      name: user?.name || '',
      email: user?.email || '',
      phone: user?.phone || '',
      street: '',
      city: '',
      state: '',
      zipCode: '',
      needBy: savedDate,
    };
  });
  const [paymentMethod] = useState('razorpay');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const subtotal = cart.reduce((sum, item) => sum + Number(item.price || 0) * Number(item.quantity || 1), 0);
  const deliveryDiscount = useMemo(() => {
    const today = new Date();
    const selectedDate = form.needBy ? new Date(`${form.needBy}T00:00:00`) : new Date();
    const daysAhead = Math.max(0, Math.ceil((selectedDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)));
    return daysAhead >= 30 ? getDeliveryDiscount(subtotal) : 0;
  }, [form.needBy, subtotal]);
  const finalSubtotal = Math.max(0, subtotal - deliveryDiscount);
  const shippingCost = cart.reduce((sum, item) => sum + Number(item.shippingCharge ?? 400), 0);

  if (!user) return <Navigate to="/login" replace />;
  if (cart.length === 0) return <Navigate to="/cart" replace />;

  const handleChange = (event) => setForm({ ...form, [event.target.name]: event.target.value });

  const askOnWhatsApp = () => {
    const productLines = cart.map((item) => {
      const productLink = `${window.location.origin}/product/${item._id}`;
      const options = [item.size && `Size: ${item.size}`, item.ageGroup && `Age: ${item.ageGroup}`]
        .filter(Boolean)
        .join(', ');
      return `${item.name} x${Number(item.quantity || 1)}${options ? ` (${options})` : ''}\n${productLink}`;
    }).join('\n\n');
    const message = `Hi, I would like to confirm these products before placing my order:\n\n${productLines}\n\nDelivery date: ${form.needBy}`;
    window.open(`https://wa.me/918309158686?text=${encodeURIComponent(message)}`, '_blank', 'noopener,noreferrer');
  };

  const createOrderPayload = () => ({
    items: cart.map((item) => ({
      productId: item._id,
      name: item.name,
      quantity: Number(item.quantity || 1),
      size: item.size,
      color: item.color,
      ageGroup: item.ageGroup || '1-3',
      price: Number(item.price || 0)
    })),
    totalAmount: finalSubtotal,
    discountAmount: deliveryDiscount,
    shippingCost,
    paymentMethod,
    deliveryDate: form.needBy,
    shippingAddress: {
      street: form.street,
      city: form.city,
      state: form.state,
      zipCode: form.zipCode,
      country: 'India'
    },
    customerDetails: { name: form.name, email: form.email, phone: user.whatsappPhone || form.phone }
  });

  const placeOrder = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');

    try {
      const payload = createOrderPayload();
      const orderResponse = await orderAPI.createOrder({ ...payload, clearCart: false });
      const createdOrder = orderResponse.data.order;

      const razorpayResponse = await paymentAPI.createRazorpayOrder({
        amount: createdOrder.finalAmount,
        orderId: createdOrder._id
      });

      const razorpayKey = razorpayResponse.data.order?.key_id;
      const amountInPaise = Math.round((createdOrder.finalAmount || 0) * 100);

      if (!window.Razorpay || !razorpayKey) {
        throw new Error('Razorpay checkout is not available right now.');
      }

      const razorpay = new window.Razorpay({
        key: razorpayKey,
        amount: amountInPaise,
        currency: 'INR',
        name: 'Starlight Threading',
        description: 'Order payment',
        order_id: razorpayResponse.data.order.id,
        handler: async function (successResponse) {
          try {
            await paymentAPI.verifyPayment({
              razorpay_order_id: successResponse.razorpay_order_id,
              razorpay_payment_id: successResponse.razorpay_payment_id,
              razorpay_signature: successResponse.razorpay_signature,
              orderId: createdOrder._id
            });
            await clearCart();
            navigate('/account', { replace: true });
          } catch (verifyError) {
            setError(verifyError.response?.data?.message || 'Payment verification failed. Please contact support.');
          } finally {
            setLoading(false);
          }
        },
        prefill: {
          name: form.name,
          email: form.email,
          contact: user.whatsappPhone || form.phone
        },
        theme: {
          color: '#c94f7c'
        },
        modal: {
          ondismiss: () => setLoading(false)
        }
      });

      razorpay.on('payment.failed', (failureResponse) => {
        setError(failureResponse.error?.description || 'Payment failed. Please try again.');
        setLoading(false);
      });
      razorpay.open();
    } catch (requestError) {
      setError(requestError.response?.data?.message || requestError.message || 'Unable to place your order. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="checkout-page">
      <div className="checkout-header">
        <p className="eyebrow">Secure checkout</p>
        <h1>Complete your order</h1>
        <p>Signed in as {user.email}</p>
      </div>
      <form className="checkout-form" onSubmit={placeOrder}>
        <section className="checkout-section">
          <h2>Shipping Information</h2>
          <label>Full Name<input name="name" required value={form.name} onChange={handleChange} /></label>
          <div className="checkout-grid">
            <label>Email<input type="email" name="email" required value={form.email} onChange={handleChange} /></label>
            <label>Phone<input name="phone" required value={form.phone} onChange={handleChange} /></label>
          </div>
          <label>Complete delivery address
            <input
              name="street"
              required
              minLength="10"
              value={form.street}
              onChange={handleChange}
              placeholder="House / flat no., building, street, and landmark"
            />
            <small className="field-help field-help-alert">Please provide the complete address where your order should be delivered, including a nearby landmark.</small>
          </label>
        </section>
        <div className="checkout-grid">
          <label>City<input name="city" required minLength="2" value={form.city} onChange={handleChange} placeholder="Your city" /></label>
          <label>State<input name="state" required minLength="2" value={form.state} onChange={handleChange} placeholder="Your state" /></label>
          <label>PIN code<input name="zipCode" required pattern="[0-9]{6}" inputMode="numeric" value={form.zipCode} onChange={handleChange} placeholder="6-digit PIN code" title="Enter a valid 6-digit PIN code" /></label>
        </div>
        <div className="shipping-note checkout-rule">Shipping is calculated from the products in your order.</div>
        <label>Payment method
          <input value="Razorpay (cards, UPI, net banking)" readOnly />
        </label>
        {error && <p className="auth-error">{error}</p>}
        <div className="checkout-total"><span>Subtotal</span><strong>₹{subtotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</strong></div>
        {deliveryDiscount > 0 && <div className="checkout-total"><span>Advance booking discount</span><strong>-₹{deliveryDiscount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</strong></div>}
        <div className="checkout-total"><span>Shipping</span><strong>₹{shippingCost.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</strong></div>
        <div className="checkout-total total-emphasis"><span>Total</span><strong>₹{(finalSubtotal + shippingCost).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</strong></div>
        <button className="whatsapp-btn" type="button" onClick={askOnWhatsApp}>Ask on WhatsApp</button>
        <button className="primary-btn" type="submit" disabled={loading}>{loading ? 'Preparing payment...' : 'Pay with Razorpay'}</button>
      </form>
    </div>
  );
}
